import { create } from "zustand";
import {
  MAX_JOURNEY_DAYS,
  STORAGE_KEY,
  clampSentences,
  clampWords,
  createSettings,
  defaultSentenceCount,
  persistedAppStateSchema,
  sheetSettingsSchema,
  todayInTimezone,
  type AccessState,
  type LanguageCode,
  type PersistedAppState,
  type ProgressState,
  type SheetSettings,
} from "../shared";

type HydrationState = "idle" | "ready" | "corrupt";

interface AppStoreState extends PersistedAppState {
  hydrationState: HydrationState;
  hydrationMessage: string | null;
  persistenceWarning: string | null;
  hydrate: () => void;
  startJourney: (settings: SheetSettings) => void;
  updateSettings: (update: Partial<SheetSettings>) => boolean;
  toggleCompletedDay: (day: number, currentDay: number) => void;
  restart: () => void;
  joinEarlyList: (email: string) => void;
  unlockFullCourse: () => void;
  clearCorruptStateAndRestart: () => void;
}

const INITIAL_PERSISTED_STATE: PersistedAppState = {
  schemaVersion: 2,
  settings: null,
  progress: { completedDays: [] },
  access: { unlocked: false, unlockedAt: null, email: null },
};

const LEGACY_LESSON_SIZE_WORDS: Record<string, number> = {
  compact: 3,
  standard: 5,
  extended: 6,
};

/**
 * Reads the current envelope, and folds the old household envelope (schema 1,
 * up to four learners with three-language print presets) down to the two
 * languages that shaped it. Unknown versions are never guessed or overwritten.
 */
export function migratePersistedAppState(
  value: unknown,
): PersistedAppState | null {
  const current = persistedAppStateSchema.safeParse(value);
  if (current.success) return current.data;

  if (!value || typeof value !== "object") return null;
  const candidate = value as Record<string, unknown>;
  if (candidate.schemaVersion !== 1) return null;

  const household = candidate.household as
    | {
        timezone?: string;
        startDate?: string;
        lessonSize?: string;
        learners?: { name?: string; targetLanguage?: LanguageCode }[];
        togetherPreset?: { languageOrder?: LanguageCode[] };
      }
    | null
    | undefined;

  if (!household) {
    return {
      ...INITIAL_PERSISTED_STATE,
      progress: readLegacyProgress(candidate),
    };
  }

  const learners = household.learners ?? [];
  const order = household.togetherPreset?.languageOrder ?? [];
  const [left, right] = [
    learners[0]?.targetLanguage ?? order[0] ?? "th",
    learners[1]?.targetLanguage ?? order.find((code) => code !== order[0]) ?? "nl",
  ];

  const words = LEGACY_LESSON_SIZE_WORDS[household.lessonSize ?? "standard"] ?? 5;
  const settings = createSettings(left, right === left ? "nl" : right, {
    leftLabel: learners[0]?.name ?? "",
    rightLabel: learners[1]?.name ?? "",
    words,
    sentences: defaultSentenceCount(words),
    timezone: household.timezone,
    startDate: household.startDate,
  });

  const parsed = sheetSettingsSchema.safeParse(settings);
  if (!parsed.success) return null;

  return {
    schemaVersion: 2,
    settings: parsed.data,
    progress: readLegacyProgress(candidate),
    access: { unlocked: false, unlockedAt: null, email: null },
  };
}

function readLegacyProgress(candidate: Record<string, unknown>): ProgressState {
  const progress = candidate.progress as { completedDays?: unknown } | undefined;
  const days = Array.isArray(progress?.completedDays)
    ? progress.completedDays.filter(
        (day): day is number => typeof day === "number",
      )
    : [];
  return { completedDays: uniqueSortedDays(days) };
}

function uniqueSortedDays(days: number[]): number[] {
  return [...new Set(days)]
    .filter((day) => Number.isInteger(day) && day >= 1 && day <= MAX_JOURNEY_DAYS)
    .sort((a, b) => a - b);
}

function snapshot(state: AppStoreState): PersistedAppState {
  return {
    schemaVersion: 2,
    settings: state.settings,
    progress: { completedDays: uniqueSortedDays(state.progress.completedDays) },
    access: state.access,
  };
}

function writeSnapshot(value: PersistedAppState): string | null {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    return null;
  } catch {
    return "This browser blocked local storage, so changes last only until you close the tab.";
  }
}

const STORAGE_BLOCKED =
  "This browser blocked local storage, so changes last only until you close the tab.";
const UNREADABLE =
  "Your saved SprinkleDays setup could not be read. Nothing has been deleted.";

export const useAppStore = create<AppStoreState>((set, get) => {
  const persistCurrent = () => {
    const warning = writeSnapshot(snapshot(get()));
    if (warning !== get().persistenceWarning) {
      set({ persistenceWarning: warning });
    }
  };

  return {
    ...INITIAL_PERSISTED_STATE,
    hydrationState: "idle",
    hydrationMessage: null,
    persistenceWarning: null,

    hydrate: () => {
      let raw: string | null = null;
      try {
        raw = window.localStorage.getItem(STORAGE_KEY);
      } catch {
        set({
          ...INITIAL_PERSISTED_STATE,
          hydrationState: "ready",
          persistenceWarning: STORAGE_BLOCKED,
        });
        return;
      }

      if (!raw) {
        set({ ...INITIAL_PERSISTED_STATE, hydrationState: "ready" });
        return;
      }

      try {
        const migrated = migratePersistedAppState(JSON.parse(raw));
        if (!migrated) {
          set({ hydrationState: "corrupt", hydrationMessage: UNREADABLE });
          return;
        }
        set({ ...migrated, hydrationState: "ready", hydrationMessage: null });
      } catch {
        set({ hydrationState: "corrupt", hydrationMessage: UNREADABLE });
      }
    },

    startJourney: (settings) => {
      const parsed = sheetSettingsSchema.parse(settings);
      set({
        settings: parsed,
        progress: { completedDays: [] },
        hydrationState: "ready",
        hydrationMessage: null,
      });
      persistCurrent();
    },

    updateSettings: (update) => {
      const settings = get().settings;
      if (!settings) return false;

      const next = { ...settings, ...update };
      if (update.words !== undefined) {
        next.words = clampWords(update.words);
        // Sentence count follows the words unless it was set by hand.
        if (update.sentences === undefined) {
          next.sentences =
            settings.sentences === defaultSentenceCount(settings.words)
              ? defaultSentenceCount(next.words)
              : settings.sentences;
        }
      }
      if (update.sentences !== undefined) {
        next.sentences = clampSentences(update.sentences);
      }
      if (update.left !== undefined && update.right === undefined) {
        if (next.left === next.right) next.right = settings.left;
      }
      if (update.right !== undefined && update.left === undefined) {
        if (next.left === next.right) next.left = settings.right;
      }

      const parsed = sheetSettingsSchema.safeParse(next);
      if (!parsed.success) return false;

      set({ settings: parsed.data });
      persistCurrent();
      return true;
    },

    toggleCompletedDay: (day, currentDay) => {
      if (!Number.isInteger(day) || day < 1 || day > currentDay) return;
      const completedDays = get().progress.completedDays;
      const next = completedDays.includes(day)
        ? completedDays.filter((value) => value !== day)
        : [...completedDays, day];
      set({ progress: { completedDays: uniqueSortedDays(next) } });
      persistCurrent();
    },

    restart: () => {
      const settings = get().settings;
      if (!settings) return;
      set({
        settings: {
          ...settings,
          startDate: todayInTimezone(settings.timezone),
        },
        progress: { completedDays: [] },
      });
      persistCurrent();
    },

    /**
     * Recorded the moment the list accepts the address, before the course is
     * opened, so closing the tab on the confirmation costs the learner nothing
     * and never posts the same person twice.
     */
    joinEarlyList: (email) => {
      const trimmed = email.trim();
      if (!trimmed) return;
      set({ access: { ...get().access, email: trimmed } });
      persistCurrent();
    },

    unlockFullCourse: () => {
      const access: AccessState = {
        ...get().access,
        unlocked: true,
        unlockedAt: new Date().toISOString(),
      };
      set({ access });
      persistCurrent();
    },

    clearCorruptStateAndRestart: () => {
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch {
        // The in-memory restart still lets the app run for this tab.
      }
      set({
        ...INITIAL_PERSISTED_STATE,
        hydrationState: "ready",
        hydrationMessage: null,
      });
    },
  };
});
